// src/features/scope_module/components/ConclusionDropdown.js
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronDown, Check, AlertCircle } from 'lucide-react';

function ConclusionDropdown({
  options = [],
  value,
  onChange,
  disabled = false,
  highlight = false,
  placeholder = 'Conclusion'
}) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Close on escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen]);

  const handleToggle = useCallback(() => {
    if (!disabled) {
      setIsOpen(prev => !prev);
    }
  }, [disabled]);

  const handleSelect = useCallback((opt) => {
    if (onChange) {
      // Mimic native select event shape
      onChange({ target: { value: opt } });
    }
    setIsOpen(false);
  }, [onChange]);

  // Get styling class based on conclusion type
  const getConclusionClass = (opt) => {
    const lower = (opt || '').toLowerCase();
    if (lower === 'identification') return 'conclusion-identification';
    if (lower === 'exclusion') return 'conclusion-exclusion';
    if (lower === 'inconclusive') return 'conclusion-inconclusive';
    if (lower.includes('support') && lower.includes('identification')) return 'conclusion-support-id';
    if (lower.includes('support') && lower.includes('exclusion')) return 'conclusion-support-ex';
    return '';
  };

  const selectedLabel = value || placeholder;
  const hasValue = !!value;

  return (
    <div
      ref={containerRef}
      className={`conclusion-dropdown ${disabled ? 'disabled' : ''} ${highlight ? 'pulse-highlight' : ''} ${isOpen ? 'open' : ''}`}
    >
      <button
        type="button"
        className={`conclusion-dropdown-trigger ${hasValue ? getConclusionClass(value) : ''}`}
        onClick={handleToggle}
        disabled={disabled}
        title={disabled ? 'Conclusion selection disabled' : 'Select conclusion'}
      >
        {hasValue && <AlertCircle size={12} className="conclusion-icon" />}
        <span className={`conclusion-dropdown-label ${!hasValue ? 'placeholder' : ''}`}>
          {selectedLabel}
        </span>
        <ChevronDown
          size={14}
          className={`conclusion-dropdown-chevron ${isOpen ? 'rotated' : ''}`}
        />
      </button>

      {isOpen && !disabled && (
        <div className="conclusion-dropdown-menu">
          <div className="conclusion-dropdown-header">Select Conclusion</div>
          <div className="conclusion-dropdown-options">
            {options.map((opt) => (
              <button
                key={opt}
                type="button"
                className={`conclusion-dropdown-option ${getConclusionClass(opt)} ${value === opt ? 'selected' : ''}`}
                onClick={() => handleSelect(opt)}
              >
                <span className="conclusion-option-text">{opt}</span>
                {value === opt && <Check size={14} className="conclusion-check" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default ConclusionDropdown;
